import { IconCalendar, IconMail, IconMoneybag } from '@tabler/icons-react';
import { Button } from '@zorro/design-system/v2/Button';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Icon, Stack, brand } from '@zorro/web/design-system';
import { useMonolithMutation, useMonolithQuery } from '@zorro/web/ui-utils';

import { EnrollmentAction } from '../../../EmployeePageHeader';
import { EmployeePagePlansActionButtons } from './EmployeePagePlansActionButtons';
import { EmployeePagePlansAllowanceInfo } from './EmployeePagePlansAllowanceInfo';
import { EmployeePagePlansCard } from './EmployeePagePlansCard';
import { formatElectionWindowSubtitle } from './EmployeePagePlansUtils';

type Props = {
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
  onboardingPeriodId: string;
  employeeId: string;
  title: string;
  withSendReminderButton?: boolean;
  withActiveImpersonationButton?: boolean;
};

export const EmployeePagePlansElectionActive = ({
  onEnrollmentTabActionChange,
  onboardingPeriodId,
  employeeId,
  title,
  withSendReminderButton = false,
  withActiveImpersonationButton = false,
}: Props) => {
  const { mutate: sendReminder } = useMonolithMutation({
    method: 'enrollmentCommunicationsControllerSendElectionReminder',
    successMessage: 'Reminder sent',
  });
  const { data: onboardingPeriod, isFetching } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  if (!onboardingPeriod || isFetching) {
    return null;
  }

  return (
    <Stack w="100%">
      <EmployeePagePlansCard
        icon={<IconCalendar />}
        title={title}
        subtitle={formatElectionWindowSubtitle(onboardingPeriod)}
        rightSection={
          withSendReminderButton ? (
            <Button
              leftSection={<Icon icon={IconMail} color={brand.zorroWhite} />}
              onClick={() => sendReminder([onboardingPeriodId])}
            >
              Send a reminder
            </Button>
          ) : undefined
        }
        withDivider
      />
      <EmployeePagePlansCard
        icon={<IconMoneybag />}
        title="Monthly allowance"
        subtitle="The amount of the stipend depends on whom you choose to cover"
        rightSection={
          <EmployeePagePlansAllowanceInfo
            onboardingPeriodId={onboardingPeriodId}
          />
        }
      />
      <Divider label="Available actions" labelPosition="center" my="xs" />
      <EmployeePagePlansActionButtons
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        enrollmentStatus={onboardingPeriod.enrollmentStatus}
        onWaiveCoverageClick={() =>
          onEnrollmentTabActionChange(EnrollmentAction.WAIVE_COVERAGE)
        }
        onManualElectionClick={() =>
          onEnrollmentTabActionChange(EnrollmentAction.MANUAL_SUBMISSION)
        }
        withActiveImpersonationButton={withActiveImpersonationButton}
      />
    </Stack>
  );
};
